/**
 * Project Dashboard mappers.
 * Keep API-shape parsing and display mapping out of components/thunks.
 */

const CHART_COLORS = [
  "#22c55e",
  "#f97316",
  "#2563eb",
  "#9333ea",
  "#64748b",
  "#0ea5e9",
  "#a855f7",
  "#cd2c51fc",
  "#09b570",
];

function colorAt(i) {
  return CHART_COLORS[i % CHART_COLORS.length];
}

export function getProjectDashboardModel(rawResponse) {
  // Expected API wrapper: { success: true, data: { ...dashboard... } }
  const payload = rawResponse?.data ?? rawResponse;
  const data = payload && typeof payload === "object" ? payload : {};

  const project = data.project ?? null;
  const summary = data.summary ?? null;
  const issues = data.issues ?? null;
  const team = data.team ?? null;
  const activity = Array.isArray(data.activity) ? data.activity : [];
  const meta = data.meta ?? null;

  const statusChartData = Array.isArray(issues?.byStatus)
    ? issues.byStatus.map((s, i) => ({
        name: s.statusName ?? s.category ?? "Status",
        value: Number(s.count) || 0,
        color: colorAt(i),
      }))
    : [];

  const priorityChartData = Array.isArray(issues?.byPriority)
    ? issues.byPriority.map((p, i) => ({
        name: String(p.priority ?? "PRIORITY").replace(/_/g, " "),
        value: Number(p.count) || 0,
        color: colorAt(i),
      }))
    : [];

  const typeChartData = Array.isArray(issues?.byType)
    ? issues.byType.map((t, i) => ({
        name: String(t.type ?? "TYPE").replace(/_/g, " "),
        value: Number(t.count) || 0,
        color: colorAt(i),
      }))
    : [];

  const summaryCards = {
    totalTime: summary?.totalFormatted ?? null,
    totalMinutes: summary?.totalMinutes == null ? null : Number(summary.totalMinutes) || 0,
    overdueCount: issues?.overdueCount == null ? null : Number(issues.overdueCount) || 0,
    dueSoonCount: issues?.dueSoonCount == null ? null : Number(issues.dueSoonCount) || 0,
    totalMembers: team?.totalMembers == null ? null : Number(team.totalMembers) || 0,
    hoursLimit: project?.hoursLimit == null ? null : Number(project.hoursLimit) || 0,
    startDate: project?.startDate ?? null,
    endDate: project?.endDate ?? null,
    status: project?.status ?? null,
  };

  const members = Array.isArray(team?.members) ? team.members : [];

  const activities = activity.map((a) => {
    const by = a?.by ?? {};
    const metaObj = a?.meta ?? {};
    const actorName = by?.name ?? by?.email ?? "User";
    const type = a?.type ?? "ACTIVITY";
    const titleBase = (() => {
      if (type === "TIME_LOG_ADDED") {
        const work = [metaObj.workItemKey, metaObj.workItemTitle].filter(Boolean).join(" · ");
        const mins = metaObj.minutesLogged != null ? `${metaObj.minutesLogged}m` : "";
        return ["Time logged", work, mins].filter(Boolean).join(" — ");
      }
      if (type === "ISSUE_UPDATED") {
        const work = [metaObj.workItemKey, metaObj.workItemTitle].filter(Boolean).join(" · ");
        return ["Issue updated", work].filter(Boolean).join(" — ");
      }
      return type.replace(/_/g, " ");
    })();

    return {
      id: `${a?.type ?? "ACT"}-${a?.at ?? ""}-${by?.id ?? ""}`,
      actorName,
      actorEmail: by?.email ?? "",
      title: titleBase,
      at: a?.at ?? "",
      meta: metaObj,
    };
  });

  return {
    project,
    summary,
    issues,
    team,
    activity: activities,
    meta,
    charts: {
      status: statusChartData,
      priority: priorityChartData,
      type: typeChartData,
    },
    cards: summaryCards,
    members,
  };
}

/** Display name for project workspace chrome (header) — same unwrap as {@link getProjectDashboardModel}. */
export function getWorkspaceTitleFromDashboardApiPayload(rawResponse) {
  const project = getProjectDashboardModel(rawResponse)?.project;
  if (!project) return null;
  const n = project.name ?? project.projectName;
  if (n == null || String(n).trim() === "") return null;
  return String(n).trim();
}
